import type { AnyDocumentId, DocHandle } from '@automerge/automerge-repo/slim'
import type { WorldDoc } from '@mod/core/model'
import {
  bindTargetKeys,
  readTargetMembership,
  signTargetPresence,
  type TargetPresence,
  verifyTargetPresence,
} from '@mod/core/sharing'
import { useEffect, useState } from 'react'
import { useTabRepoBoot } from '../world/repo/TabRepoProvider'
import { useSharingAccount } from './accountContext'
import {
  invitedPeople,
  type KnownIdentities,
  type MemberRow,
  projectMembership,
} from './membership'

/** Agent-key hints off a session document. Any member can write these, so
 * they are only candidates: `bindTargetKeys` keeps one only when the hinted
 * person's device signed the key's grant. */
export function agentKeyHints(doc: unknown): Record<string, string> {
  const keys = (doc as { agentKeys?: unknown } | undefined)?.agentKeys
  const hints: Record<string, string> = {}
  if (!keys || typeof keys !== 'object') return hints
  for (const [key, record] of Object.entries(keys)) {
    const r = record as { person?: unknown; revokedAt?: unknown } | undefined
    if (r?.person == null || r.revokedAt != null) continue
    hints[key] = String(r.person)
  }
  return hints
}

export function useTargetMembership(
  target: string,
  kind?: 'session' | 'project' | 'document',
) {
  const { sharing } = useTabRepoBoot()
  const account = useSharingAccount()
  const [state, setState] = useState<{
    target: string
    rows: MemberRow[]
    level: number
    ownerGroups?: string[]
    error?: string
  }>({ target, rows: [], level: 0 })
  useEffect(() => {
    setState({ target, rows: [], level: 0 })
    if (!sharing || !account) return
    let stopped = false
    let refreshing = false
    let dirty = false
    const presence = new Map<string, TargetPresence>()
    let release = () => {}
    let targetHandle: DocHandle<unknown> | undefined
    let worldHandle: DocHandle<WorldDoc> | undefined
    const self = Array.from(account.opened.person.toBytes(), (b) =>
      b.toString(16).padStart(2, '0'),
    ).join('')
    /** Names from the viewer's own records and signed grants only. A name
     * anyone could write into the target never reaches the list. */
    const identities = async (
      membership: Awaited<ReturnType<typeof readTargetMembership>>,
    ): Promise<KnownIdentities> => {
      const invites = worldHandle?.doc()?.sharingInvites
      const people = [self, ...invitedPeople(invites).keys()]
      for (const p of presence.values()) if (!p.session) people.push(p.person)
      const hints = kind === 'session' ? agentKeyHints(targetHandle?.doc()) : {}
      let keys: KnownIdentities['keys']
      try {
        keys = await bindTargetKeys(sharing, membership, people, hints)
      } catch {
        /* Unbound keys stay listed under their ids. */
      }
      return {
        self: { person: self, name: String(account.account.name ?? '') },
        ...(invites ? { invites } : {}),
        ...(keys ? { keys } : {}),
        ...(kind === 'session' ? { session: target } : {}),
      }
    }
    const refresh = async () => {
      if (refreshing) {
        dirty = true
        return
      }
      refreshing = true
      try {
        do {
          dirty = false
          const membership = await readTargetMembership(
            sharing.hive.keyhive,
            target,
          )
          const known = await identities(membership)
          // Revalidate live devices after a revoke, even before their heartbeat expires.
          for (const [key, p] of presence) {
            if (!(await verifyTargetPresence(sharing, target, p)))
              presence.delete(key)
          }
          if (!stopped)
            setState({
              target,
              rows: projectMembership(
                membership,
                [...presence.values()],
                Date.now(),
                known,
              ),
              level: membership.ownLevel,
              ownerGroups: membership.ownerGroups,
            })
        } while (dirty && !stopped)
      } catch {
        if (!stopped)
          setState({
            target,
            rows: [],
            level: 0,
            error: 'Access list unavailable. Waiting for verified grants.',
          })
      } finally {
        refreshing = false
      }
    }
    void (async () => {
      try {
        // The worker must hold this target to relay its ephemeral presence.
        sharing.requestDocument(target)
        const handle = await sharing.repo.find(target as AnyDocumentId)
        if (stopped) return
        targetHandle = handle
        // A session's thread changes on every message; re-read only when its
        // agent keys do.
        let hinted = JSON.stringify(agentKeyHints(handle.doc()))
        const hintsChanged = () => {
          const next = JSON.stringify(agentKeyHints(handle.doc()))
          if (next === hinted) return
          hinted = next
          void refresh()
        }
        try {
          worldHandle = await sharing.repo.find<WorldDoc>(
            account.opened.location.worldId as AnyDocumentId,
          )
        } catch {
          /* Without the World, invited people stay unnamed, still listed. */
        }
        if (stopped) return
        const receive = ({ message }: { message: unknown }) => {
          void verifyTargetPresence(sharing, target, message)
            .then((p) => {
              if (!p || stopped) return
              const previous = presence.get(p.signer)
              if (previous && previous.at >= p.at) return
              presence.set(p.signer, p)
              void refresh()
            })
            .catch(() => {})
        }
        const beat = async () => {
          if (stopped) return
          try {
            if (document.visibilityState !== 'hidden') {
              const person = self
              const p = await signTargetPresence(
                sharing,
                target,
                person,
                account.account.name,
              )
              if (stopped) return
              if (await verifyTargetPresence(sharing, target, p)) {
                presence.set(p.signer, p)
                handle.broadcast(p)
              }
            }
          } catch {
            /* A disconnected transport must not hide verified access. */
          }
          await refresh()
        }
        handle.on('ephemeral-message', receive)
        sharing.hive.emitter.on('update', refresh)
        worldHandle?.on('change', refresh)
        if (kind === 'session') handle.on('change', hintsChanged)
        const timer = setInterval(() => void beat(), 10000)
        document.addEventListener('visibilitychange', beat)
        release = () => {
          clearInterval(timer)
          handle.off('ephemeral-message', receive)
          sharing.hive.emitter.off('update', refresh)
          worldHandle?.off('change', refresh)
          handle.off('change', hintsChanged)
          document.removeEventListener('visibilitychange', beat)
        }
        await beat()
      } catch {
        await refresh()
      }
    })()
    return () => {
      stopped = true
      release()
    }
  }, [sharing, account, target, kind])
  return state.target === target ? state : { target, rows: [], level: 0 }
}
